import { useRouter } from 'next/router';
import { getDataUTC, getDataUTCISO, isPeli, getSlug, removeHtmlTags, dataResources } from '@portal-internet/core';
import { getUrlFriendly } from 'components/3catinfo/Enllac/RouteResolver';
import { getImatge } from 'components/3catinfo/Imatge';
import { getItemsMenu } from 'components/3catinfo/Menu/funcions';
import __escape from 'lodash/escape';
import __isEmpty from 'lodash/isEmpty';

const CATINFO_NAME = '/3catinfo/';
const SCHEMA_ORG = 'https://schema.org';

const _getCurrentUrl = (router) => {
    let url = router.asPath,
        indexParams = url.indexOf('?');
    url = indexParams > 0 ? url.substring(0, indexParams) : url;
    return `https://${dataResources.domainCCMA}${url}`;
};

const getUrl = (item) => {
    const base = `https://${dataResources.domainCCMA}`;
    const url = item.href
        ? item.href
        : getUrlFriendly({
              routePath: item.type,
              id: item.id,
              slug: item.slug,
              producte: 'web_noticies'
          });
    return base + url.replace(base, '').replace(base.replace('https', 'http'), '');
};

const _getAutors = (autors, redactor = '') => {
    const itemsAutor = [];

    if (autors) {
        autors?.map((item) => {
            const itemAutor = {
                '@type': 'Person'
            };
            const autorName = item.nom;
            const autorUrl = getUrl({ ...item, type: 'web_actualitat_item_autor', slug: autorName });

            itemAutor['name'] = autorName;
            itemAutor['url'] = autorUrl;
            itemsAutor.push(itemAutor);
        });
    } else {
        itemsAutor.push({
            '@type': 'Person',
            'name': redactor ?? 'Redacció 3CatInfo',
            'url': 'https://www.3cat.cat/3catinfo/analisi/'
        });
    }

    return itemsAutor;
};

const _getPublisher = (parentPublisher = false) => {
    const base = `https://${dataResources.domainCCMA}/`;
    const statics = 'https://statics.3cat.cat/recursos/img/logos/metadades/';

    return {
        logoUrl: parentPublisher ? statics + '3cat.png' : statics + '3catinfo.png',
        logoWidth: parentPublisher ? 302 : 300,
        logoHeight: parentPublisher ? 95 : 68,
        logoName: parentPublisher ? '3Cat' : '3CatInfo',
        urlPublisher: parentPublisher ? base : base + '3catinfo/'
    };
};

const _getBlogSchema = (props, router) => {
    const {
        type,
        titol,
        entradeta,
        dataCreacio,
        dataModificacio,
        finalitzat,
        nomPrograma,
        imatges,
        formats,
        entrades,
        autors,
        redactor
    } = props;

    let dadesAutor = _getAutors(autors, redactor);
    let dadesPublisher = _getPublisher();

    const getThumbnail = (posMida = 0) => {
        const arrMides = ['120x68', '200x113', '320x180', '326x184', '426x240', '670x378'];
        if (posMida >= arrMides.length || !imatges) return '';
        const imatge = imatges.find(({ mida }) => mida === arrMides[posMida]);
        if (imatge) return imatge.text;
        return getThumbnail(posMida + 1);
    };

    const getName = () => {
        const prefix = !isPeli(formats) && nomPrograma ? `${nomPrograma} - ` : '';
        return prefix + titol;
    };

    const schemaData = {
        '@context': SCHEMA_ORG,
        '@type': type,
        '@id': _getCurrentUrl(router) + '#liveBlogPosting',
        'name': getName(),
        'headline': getName(),
        'description': removeHtmlTags(entradeta),
        'thumbnailUrl': getThumbnail(),
        'url': _getCurrentUrl(router),
        'coverageEndTime': '',
        'publisher': {
            '@type': 'Organization',
            'name': dadesPublisher.logoName,
            'url': dadesPublisher.urlPublisher,
            'logo': {
                '@type': 'ImageObject',
                'url': dadesPublisher.logoUrl,
                'height': dadesPublisher.logoHeight,
                'width': dadesPublisher.logoWidth
            }
        },
        'author': [dadesAutor],
        'about': {
            '@type': 'Event',
            'description': removeHtmlTags(entradeta),
            'name': getName(),
            'image': { '@type': 'ImageObject', 'url': imatges ? imatges?.[0]?.text : '', 'width': 1024, 'height': 576 },
            'location': {
                '@type': 'Place',
                'name': dadesPublisher.logoName,
                'address': { '@type': 'PostalAddress', 'addressCountry': 'ES' }
            }
        },
        'liveBlogUpdate': []
    };

    if (dataCreacio) {
        schemaData['datePublished'] = getDataUTC(dataCreacio);
        schemaData['coverageStartTime'] = getDataUTC(dataCreacio);
        schemaData['about']['startDate'] = getDataUTCISO(dataCreacio, 'iniciMinut');
    }
    if (dataModificacio) {
        schemaData['dateModified'] = getDataUTC(dataModificacio);
        schemaData['about']['endDate'] = getDataUTCISO(dataModificacio, 'fiDia');
    }
    if (finalitzat) schemaData['coverageEndTime'] = getDataUTC(dataModificacio);

    if (entrades && entrades.length > 0) {
        entrades?.map((item, index) => {
            const liveBlogItem = {
                '@type': 'BlogPosting',
                'headline': item.titol,
                'articleBody': removeHtmlTags(item.entradeta),
                'url': _getCurrentUrl(router) + '?minut=' + item.id,
                'image': {
                    '@type': 'ImageObject',
                    '@id': item.imatges ? item.imatges?.[0]?.text : '',
                    'width': 670,
                    'height': 378
                },
                'mainEntityOfPage': {
                    '@type': 'WebPage',
                    '@id': _getCurrentUrl(router)
                }
            };
            if (item.data_publicacio) liveBlogItem['datePublished'] = getDataUTC(item.data_publicacio);
            if (item.data_modificacio) liveBlogItem['dateModified'] = getDataUTC(item.data_modificacio);
            schemaData['liveBlogUpdate'].push(liveBlogItem);
        });
    }

    return schemaData;
};

const _getListItem = (tag, position, router, type = 'tag') => {
    let listItem = {
        '@type': 'ListItem',
        'position': position,
        'item': {}
    };
    let itemInList = {};

    if (type == 'tag') {
        itemInList = {
            '@id': `https://${dataResources.domainCCMA}${CATINFO_NAME}${getSlug(tag.desc)}/`,
            'name': tag.desc
        };
    } else {
        //és tema (llistat notis)
        itemInList = _getNoticiaObjectSchema(tag, router, type);
    }

    listItem['item'] = itemInList;
    return listItem;
};

const _getSiteNavigationSchema = (props) => {
    const { temesGenerics, informatius, temesDestacats, destacatsPortal } = props;
    const itemsMenu = getItemsMenu({ tipus: 'estatic', temesGenerics, informatius, temesDestacats, destacatsPortal });
    const schemaData = {
        '@context': SCHEMA_ORG,
        '@type': 'SiteNavigationElement',
        'name': '3catinfo',
        'url': `https://${dataResources.domainCCMA}/3catinfo/`,
        'hasPart': []
    };

    const addOpcions = (options) => {
        return Object.values(options).map((option) => {
            if (Array.isArray(option)) {
                return {
                    name: removeHtmlTags(option[0].titol),
                    hasPart: addItemMenuRecursive(option)
                };
            }
            return null;
        });
    };

    const addItem = (item) => {
        return {
            name: removeHtmlTags(item.text),
            url: getUrl(item)
        };
    };

    const isNotExcludeItem = (item) => {
        const excludeItems = ['Directes'];
        return !excludeItems.includes(item.text);
    };

    const addItemMenuRecursive = (items) => {
        return items
            .filter((item) => {
                return isNotExcludeItem(item);
            })
            .map((item) => {
                if (item.hasOwnProperty('opcions')) {
                    return {
                        name: removeHtmlTags(item.text),
                        hasPart: addOpcions(item.opcions)
                    };
                } else {
                    return addItem(item);
                }
            });
    };

    const part = addItemMenuRecursive(itemsMenu.item);
    schemaData.hasPart = part;
    return schemaData;
};

const _getBreadcrumbList = (props) => {
    const { tagPrincipal, tags } = props;
    let schemaData = {},
        countList = 1,
        itemListElement = [];
    if (tagPrincipal) {
        itemListElement.push(_getListItem(tagPrincipal, countList));
        countList++;
    }
    if (tags) {
        tags.map((tag) => {
            itemListElement.push(_getListItem(tag, countList));
            countList++;
        });
    }
    if (!__isEmpty(itemListElement)) {
        schemaData = {
            '@context': SCHEMA_ORG,
            '@type': 'BreadcrumbList',
            'numberOfItems': itemListElement.length,
            'itemListElement': itemListElement
        };
    }
    return schemaData;
};

const _getTemaItemListSchema = (props, router) => {
    const { temes } = props;
    let schemaData = {},
        countList = 1,
        itemListElement = [];

    if (temes) {
        temes.map((noticia) => {
            itemListElement.push(_getListItem(noticia, countList, router, 'tema'));
            countList++;
        });
    }
    if (!__isEmpty(itemListElement)) {
        schemaData = {
            '@context': SCHEMA_ORG,
            '@type': 'ItemList',
            'numberOfItems': itemListElement.length,
            'itemListElement': itemListElement
        };
    }
    return schemaData;
};

const _getNewsMediaOrganizationSchema = () => {
    const dadesPublisher = _getPublisher();
    const parentPublisher = _getPublisher(true);
    const sameAs = [
        'https://www.instagram.com/3catinfo/',
        'https://x.com/3CatInfo',
        'https://www.facebook.com/3CatInfo/',
        'https://www.whatsapp.com/channel/0029VbAVJGVJ3jurkbASOY07',
        'https://t.me/som3catinfo',
        'https://www.youtube.com/@3CatInfo',
        'https://ca.wikipedia.org/wiki/3CatInfo',
        'https://ca.wikipedia.org/wiki/324_(publicaci%C3%B3_digital)',
        'https://news.google.com/publications/CAAqJggKIiBDQklTRWdnTWFnNEtERE5qWVhRdVkyRjBMek15TkNnQVAB?hl=ca-ES&gl=ES&ceid=ES%3Aca'
    ];
    const schemaData = {
        '@context': SCHEMA_ORG,
        '@type': 'NewsMediaOrganization',
        'name': '3catinfo',
        'url': `https://${dataResources.domainCCMA}/3catinfo/`,
        'logo': {
            '@type': 'ImageObject',
            'url': dadesPublisher.logoUrl,
            'height': dadesPublisher.logoHeight,
            'width': dadesPublisher.logoWidth
        },
        'foundingDate': '2002-07-27',
        'alternateName': '3Cat Informatius',
        'sameAs': sameAs,
        'publishingPrinciples': `https://${dataResources.domainCCMA}/llibredestil/guia-editorial/3100467/`,
        'diversityPolicy': `https://${dataResources.domainCCMA}/llibredestil/diversitat/3100252/`,
        'correctionspolicy': `https://${dataResources.domainCCMA}/llibredestil/correccions/3100459/`,
        'ethicsPolicy': `https://${dataResources.domainCCMA}/llibredestil/principis-editorials/3100232/`,
        'verificationFactCheckingPolicy': `https://${dataResources.domainCCMA}/3catinfo/estatiques/verificacio/metodologia/`,
        'masthead': `https://${dataResources.domainCCMA}/llibredestil/organigrama/3100123/`,
        'missionCoveragePrioritiesPolicy': `https://${dataResources.domainCCMA}/llibredestil/cobertura/3100221/`,
        'contactPoint': {
            '@type': 'ContactPoint',
            'contactType': 'Newsroom',
            'email': 'bustiadenuncia@3cat.cat',
            'url': `https://${dataResources.domainCCMA}/atencio/`
        },
        'parentOrganization': {
            '@type': 'Organization',
            'name': '3cat',
            'url': parentPublisher.urlPublisher,
            'logo': {
                '@type': 'ImageObject',
                'url': parentPublisher.logoUrl,
                'height': parentPublisher.logoHeight,
                'width': parentPublisher.logoWidth
            }
        }
    };

    return schemaData;
};

const _getNewsMediaOrganizationSchemaSobreNosaltres = (props) => {
    const { descripcio } = props;
    const parentPublisher = _getPublisher(true);
    const sameAs = [
        'https://ca.wikipedia.org/wiki/3CatInfo',
        'https://es.wikipedia.org/wiki/3CatInfo',
        'https://www.wikidata.org/wiki/Q135792871',
        'https://x.com/3CatInfo',
        'https://www.facebook.com/3CatInfo/',
        'https://www.instagram.com/3catinfo/',
        'https://www.youtube.com/@3CatInfo',
        'https://www.whatsapp.com/channel/0029VbAVJGVJ3jurkbASOY07',
        'https://t.me/som3catinfo',
        'https://ca.wikipedia.org/wiki/324_(publicaci%C3%B3_digital)',
        'https://news.google.com/publications/CAAqJggKIiBDQklTRWdnTWFnNEtERE5qWVhRdVkyRjBMek15TkNnQVAB'
    ];

    const schemaData = {
        '@context': SCHEMA_ORG,
        '@type': 'NewsMediaOrganization',
        '@id': `https://${dataResources.domainCCMA}/3catinfo/#organization`,
        'name': '3catinfo',
        'alternateName': '3Cat Informatius',
        'url': `https://${dataResources.domainCCMA}/3catinfo/`,
        'description': descripcio,
        'slogan': 'Informar. Explicar. Entendre.',
        'foundingDate': '2002-07-27',
        'logo': {
            '@type': 'ImageObject',
            'url': 'https://statics.3cat.cat/recursos/img/logos/metadades/logo_3catinfo.png',
            'height': 68,
            'width': 300
        },
        'sameAs': sameAs,
        'publishingPrinciples': `https://${dataResources.domainCCMA}/llibredestil/guia-editorial/3100467/`,
        'ethicsPolicy': `https://${dataResources.domainCCMA}/llibredestil/principis-editorials/3100232/`,
        'correctionspolicy': `https://${dataResources.domainCCMA}/llibredestil/correccions/3100459/`,
        'verificationFactCheckingPolicy': `https://${dataResources.domainCCMA}/3catinfo/estatiques/verificacio/metodologia/`,
        'masthead': `https://${dataResources.domainCCMA}/llibredestil/organigrama/3100123/`,
        'missionCoveragePrioritiesPolicy': `https://${dataResources.domainCCMA}/llibredestil/cobertura/3100221/`,
        'diversityPolicy': `https://${dataResources.domainCCMA}/llibredestil/diversitat/3100252/`,
        'actionableFeedbackPolicy': `https://${dataResources.domainCCMA}/3catinfo/bustia-denuncia/`,
        'knowsAbout': [
            'Política catalana',
            'Actualitat internacional',
            'Economia',
            'Cultura catalana',
            'Societat',
            'Esports',
            'Informació de servei'
        ],
        'contactPoint': [
            {
                '@type': 'ContactPoint',
                'contactType': 'Newsroom',
                'email': 'bustiadenuncia@3cat.cat',
                'url': `https://${dataResources.domainCCMA}/bustia-denuncia/`,
                'availableLanguage': 'Catalan'
            },
            {
                '@type': 'ContactPoint',
                'contactType': 'Customer service',
                'url': `https://${dataResources.domainCCMA}/atencio/`,
                'availableLanguage': 'Catalan'
            }
        ],
        'employee': [
            { '@id': `https://${dataResources.domainCCMA}/3catinfo/#person-calatrava` },
            { '@id': `https://${dataResources.domainCCMA}/3catinfo/#person-fernandez` },
            { '@id': `https://${dataResources.domainCCMA}/3catinfo/#person-plandolit` }
        ],
        'parentOrganization': {
            '@type': 'Organization',
            'name': '3cat',
            'legalName': 'Corporació Catalana de Mitjans Audiovisuals',
            'url': parentPublisher.urlPublisher,
            'logo': {
                '@type': 'ImageObject',
                'url': 'https://statics.3cat.cat/recursos/img/logos/metadades/3cat.png',
                'height': 95,
                'width': 302
            }
        }
    };

    return schemaData;
};

const _getPersonSchema = (props) => {
    const { anchorPersona, nomPersona, posicioPersona, urlSameAsPersona } = props;

    const schemaData = {
        '@context': SCHEMA_ORG,
        '@type': 'Person',
        '@id': `https://${dataResources.domainCCMA}/3catinfo/#${anchorPersona}`,
        'name': nomPersona,
        'jobTitle': posicioPersona,
        'worksFor': { '@id': `https://${dataResources.domainCCMA}/3catinfo/#organization` },
        ...(urlSameAsPersona ? { sameAs: [urlSameAsPersona] } : {})
    };

    return schemaData;
};

const _getAboutPageSchema = (props) => {
    const { slug, titolPagina, idioma } = props;

    const schemaData = {
        '@context': SCHEMA_ORG,
        '@type': 'AboutPage',
        '@id': `https://${dataResources.domainCCMA}/3catinfo/${slug}/#webpage`,
        'url': `https://${dataResources.domainCCMA}/3catinfo/${slug}/`,
        'name': titolPagina,
        'inLanguage': idioma,
        'isPartOf': { '@id': `https://${dataResources.domainCCMA}/#website` },
        'about': { '@id': `https://${dataResources.domainCCMA}/3catinfo/#organization` },
        'mainEntity': { '@id': `https://${dataResources.domainCCMA}/3catinfo/#organization` },
        'breadcrumb': {
            '@id': `https://${dataResources.domainCCMA}/3catinfo/${slug}/#breadcrumb`
        }
    };

    return schemaData;
};

const _getBreadcrumbListSchema = (props) => {
    const { slug, titolPagina } = props;

    const schemaData = {
        '@context': SCHEMA_ORG,
        '@type': 'BreadcrumbList',
        '@id': `https://${dataResources.domainCCMA}/3catinfo/${slug}/#breadcrumb`,
        'itemListElement': [
            {
                '@type': 'ListItem',
                'position': 1,
                'name': '3Cat',
                'item': `https://${dataResources.domainCCMA}/`
            },
            {
                '@type': 'ListItem',
                'position': 2,
                'name': '3CatInfo',
                'item': `https://${dataResources.domainCCMA}/3catinfo/`
            },
            {
                '@type': 'ListItem',
                'position': 3,
                'name': titolPagina,
                'item': `https://${dataResources.domainCCMA}/3catinfo/${slug}/`
            }
        ]
    };

    return schemaData;
};

const _getProfilePageSchema = (props, router) => {
    const { id, nom, carrec, twitter, imatges, data_modificacio, data_publicacio } = props.autor;
    const schemaData = {
        '@context': SCHEMA_ORG,
        '@type': 'ProfilePage',
        'dateCreated': getDataUTC(data_publicacio),
        'dateModified': getDataUTC(data_modificacio),
        'mainEntity': {
            '@type': 'Person',
            'name': nom,
            'alternateName': twitter,
            'identifier': id,
            'description': carrec,
            'image': getImatge(imatges, 'web_autor').src,
            'sameAs': _getCurrentUrl(router)
        }
    };

    if (props.temes) {
        const hasPart = {
            '@type': 'CollectionPage',
            'mainEntityOfPage': _getCurrentUrl(router),
            'url': _getCurrentUrl(router),
            'name': nom,
            'mainEntity': _getTemaItemListSchema(props, router)
        };
        schemaData['hasPart'] = hasPart;
    }

    return schemaData;
};

const _getNoticiaObjectSchema = (props, router, type = '') => {
    const {
        titol,
        entradeta,
        cos,
        imatges,
        autors,
        redactor,
        dataModificacio,
        dataPublicacio,
        data_modificacio,
        data_publicacio,
        permatitle,
        id
    } = props;
    const getDades = (type = '') => {
        let dadesAutors = _getAutors(autors, redactor);
        let dadesPublisher = _getPublisher();
        let url = _getCurrentUrl(router);
        if (type == 'tema') {
            url = `https://${dataResources.domainCCMA}${CATINFO_NAME}${getSlug(permatitle)}/noticia/${id}/`;
        }

        return {
            autors: dadesAutors,
            logoUrl: dadesPublisher.logoUrl,
            logoWidth: dadesPublisher.logoWidth,
            logoHeight: dadesPublisher.logoHeight,
            logoName: dadesPublisher.logoName,
            imatge: getImatge(imatges, 'web_destacat', null, 1200),
            urlPublisher: dadesPublisher.urlPublisher,
            url: url
        };
    };
    const dades = getDades(type);

    const schemaData = {
        '@context': SCHEMA_ORG,
        '@type': 'NewsArticle',
        'url': dades.url,
        'description': entradeta,
        'publisher': {
            '@type': 'Organization',
            'name': dades.logoName,
            'url': dades.urlPublisher,
            'logo': {
                '@type': 'ImageObject',
                'url': dades.logoUrl,
                'height': dades.logoHeight,
                'width': dades.logoWidth
            }
        },
        'isAccessibleForFree': true,
        'inLanguage': 'ca',
        'headline': titol,
        'image': [
            {
                '@type': 'ImageObject',
                'url': dades.imatge.src,
                'width': 1200,
                'height': 677
            }
        ]
    };

    if (dades.autors[0]?.name !== '') schemaData['author'] = [dades.autors];

    if (type == 'tema') {
        if (data_publicacio) schemaData['datePublished'] = getDataUTC(data_publicacio);
        if (data_modificacio) schemaData['dateModified'] = getDataUTC(data_modificacio);
    } else {
        schemaData['mainEntityOfPage'] = dades.url;
        schemaData['articleBody'] = __escape(removeHtmlTags(cos));
        if (dataPublicacio) schemaData['datePublished'] = getDataUTC(dataPublicacio);
        if (dataModificacio) schemaData['dateModified'] = getDataUTC(dataModificacio);
    }
    return schemaData;
};

const _getOrganizationSchema = () => {
    const publisher = _getPublisher();
    const parentPublisher = _getPublisher(true);
    const schemaData = {
        '@context': SCHEMA_ORG,
        '@type': 'Organization',
        'name': '3CatInfo',
        'url': publisher.urlPublisher,
        'logo': [
            {
                '@type': 'ImageObject',
                'url': publisher.logoUrl,
                'height': publisher.logoHeight,
                'width': publisher.logoWidth
            }
        ],
        'parentOrganization': {
            '@type': 'Organization',
            'name': '3Cat',
            'url': parentPublisher.urlPublisher,
            'logo': {
                '@type': 'ImageObject',
                'url': parentPublisher.logoUrl,
                'height': parentPublisher.logoHeight,
                'width': parentPublisher.logoWidth
            }
        }
    };
    return schemaData;
};

export default function MarcatgeEstructurat(props) {
    const { type } = props;

    const router = useRouter();
    let schemaData = null;

    switch (type) {
        case 'Organization':
            schemaData = _getOrganizationSchema();
            break;
        case 'SiteNavigationElement':
            schemaData = _getSiteNavigationSchema(props);
            break;
        case 'BreadcrumbList':
            schemaData = _getBreadcrumbList(props);
            break;
        case 'NewsArticle':
            schemaData = _getNoticiaObjectSchema(props, router);
            break;
        case 'LiveBlogPosting':
            schemaData = _getBlogSchema(props, router);
            break;
        case 'TemaItemList':
            schemaData = _getTemaItemListSchema(props, router);
            break;
        case 'ProfilePage':
            schemaData = _getProfilePageSchema(props, router);
            break;
        case 'NewsMediaOrganization':
            schemaData = _getNewsMediaOrganizationSchema();
            break;
        case 'NewsMediaOrganizationSobreNosaltres':
            schemaData = _getNewsMediaOrganizationSchemaSobreNosaltres(props);
            break;
        case 'PersonSchema':
            schemaData = _getPersonSchema(props);
            break;
        case 'AboutPageSchema':
            schemaData = _getAboutPageSchema(props);
            break;
        case 'BreadcrumbListSchema':
            schemaData = _getBreadcrumbListSchema(props);
            break;
    }
    return schemaData ? (
        <>
            <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        </>
    ) : (
        <></>
    );
}
